import React from 'react';
import { useIntl } from 'react-intl';
import classNames, { Argument } from 'classnames';
import { CountryCode } from 'libphonenumber-js/max';

import { getDealerId, getPrefixByDealerId } from 'modules/dealers/selectors';
import { getSelectedLanguage } from 'modules/translations/selectors';

import { RootState } from 'App/Store';
import { useGetCountriesQuery } from 'modules/refData/service';
import { Country } from 'modules/refData/types/Countries';
import type { PhoneNumber } from 'modules/selfServices/types/SelfService';

import { useSelector } from 'hooks';
import { getCountryByPhoneNumber, validatePhoneNumber } from 'utils/phoneNumberUtils';

import Input from 'components/ui/Input';

import Spinner from './Spinner';
import CountrySelect from './CountrySelect';

interface PhoneNumberInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'onError' | 'className'> {
  className?: Argument;
  onChange: (phoneNumber: PhoneNumber) => void;
  onError?: (hasError: boolean) => void;
  value: PhoneNumber;
}

const PhoneNumberInput: React.FC<PhoneNumberInputProps> = ({
  value, onChange, className, disabled, onError, required, ...rest
}) => {
  const intl = useIntl();

  const language = useSelector(getSelectedLanguage);

  const dealerId = useSelector(getDealerId);
  const defaultPrefix = useSelector((state: RootState) => getPrefixByDealerId(state, dealerId));

  const local = value?.local ?? '';
  const prefix = value?.prefix ?? defaultPrefix?.toString();
  const { data: countries, isLoading, isSuccess } = useGetCountriesQuery({ language });
  const [selectedCountryCode, setSelectedCountryCode] = React.useState(countries?.find((country) => String(country.prefix) === prefix)?.code);

  const selectedCountry = React.useMemo(
    () => {
      if (local.length > 0) {
        const country = getCountryByPhoneNumber(value);
        return country ?? selectedCountryCode;
      }
      return selectedCountryCode;
    },
    [value, selectedCountryCode]
  );

  const [isTouched, setIsTouched] = React.useState(false);

  const { error, prefixError } = React.useMemo(() => {
    if (!isTouched || (!required && !local)) {
      return {};
    }

    let newError = validatePhoneNumber(`${prefix}${local}`, prefix, intl);
    let newPrefixError;
    if (!local) {
      newError = intl.formatMessage({ id: 'validation.required', defaultMessage: 'Please answer to continue.' });
    }
    if (!prefix) {
      newPrefixError = intl.formatMessage({ id: 'validation.prefix', defaultMessage: 'The prefix is wrong' });
    }
    return { error: newError, prefixError: newPrefixError };
  }, [intl, isTouched, local, prefix, required]);

  React.useEffect(() => {
    onError?.(Boolean(error || prefixError));
  }, [error, prefixError, onError]);

  React.useEffect(() => {
    if (isSuccess && countries?.length > 0) {
      const countryByPrefix = countries.find((country) => String(country.prefix) === prefix);
      if (countryByPrefix && countryByPrefix.code !== selectedCountryCode) {
        setSelectedCountryCode(countryByPrefix.code);
      } else if (!selectedCountryCode && !countryByPrefix) {
        setSelectedCountryCode(countries[0].code);
      }
    }
  }, [isSuccess, selectedCountryCode, countries, prefix]);

  const handleChange = React.useCallback(({ prefix: prefixParam, local: localParam }: Partial<PhoneNumber>) => {
    const newLocal = localParam ?? local;
    const newPrefix = prefixParam ?? prefix;
    onChange?.({ prefix: newPrefix, local: newLocal || undefined });
    setIsTouched(true);
  }, [local, onChange, prefix]);

  const handleCountryChange = (country: Country) => {
    setSelectedCountryCode(country.code);
    handleChange({ prefix: country.prefix.toString() });
  };

  const handleLocalChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newLocal = event.target.value;
    if (/^[0-9]{0,15}$/.test(newLocal)) {
      handleChange({ local: newLocal });
    }
  };

  const handleBlur = React.useCallback(() => {
    onChange?.({
      prefix: prefix.replace(/^\+?(00)?/, ''),
      local: local.replace(/^0/, ''),
    });
    setIsTouched(true);
  }, [local, onChange, prefix]);

  return isLoading ? <Spinner /> : (
    <div className={classNames(className, 'flex gap-4', error && 'pb-10')}>
      <div className="flex flex-col relative">
        <CountrySelect value={selectedCountry as CountryCode} onChange={handleCountryChange} label={intl.formatMessage({ id: 'customerInfo.prefix', defaultMessage: 'Prefix' })} error={prefixError} />
      </div>
      <div className="flex flex-col w-full">
        <Input
          type="tel"
          error={error}
          value={local}
          maxLength={15}
          className="w-full h-full"
          pattern="^[0-9]{3,15}$"
          data-testid="phoneNumber-input"
          autoComplete="tel"
          required={required}
          disabled={disabled}
          label={intl.formatMessage({ id: 'customerInfo.phoneNumber', defaultMessage: 'Phone number' })}
          {...rest}
          onChange={handleLocalChange}
          onBlur={handleBlur}
          errorClassname="absolute"
          inputClassName="h-full"
        />
      </div>
    </div>
  );
};

export default PhoneNumberInput;
